// Real Foinn.org screens (same Tailwind markup as the app) for the feature posts.
import { Search, ChevronDown, Check, Flag, Info, FileDown, ShieldQuestion, Star } from "lucide-react";
import { SiTidal, SiYoutube, SiSpotify, SiApplemusic, SiBandcamp } from "react-icons/si";
import { FaDeezer } from "react-icons/fa";
import { FameSlider, fameColor } from "../FameSliders";
import { TuneCard } from "../TuneCard";
import { AlbumNav } from "../AlbumNav";
import { RankingVote } from "../RankingVote";
import { AbcGuideBody } from "../AbcHelpDialog";
import { skin } from "./SocialMocks";

const VIEW = 440; // phone-width layout, blown up to the card

// zoom > 1 = fewer css px across = everything bigger
export const Screen = ({ children, dark, zoom = 1, width = 968, grow = true }) => {
  const virtual = VIEW / zoom;
  return (
    <div
      style={{
        flex: grow ? 1 : "none",
        minHeight: 0,
        overflow: "hidden",
        borderRadius: 30,
        border: `3px solid ${skin(dark).border}`,
        background: "#fdfbf7",
      }}
    >
      <div style={{ width: virtual, zoom: (width - 6) / virtual }} className="text-[#2d2824]">
        {children}
      </div>
    </div>
  );
};

const Trigger = ({ label, value }) => (
  <div>
    <label className="mb-1 block text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">{label}</label>
    <div className="flex h-12 w-full items-center justify-between rounded-2xl border border-[#e5e0d8] bg-white px-3 text-base font-semibold capitalize">
      {value}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </div>
  </div>
);

const OpenList = ({ items, selected }) => (
  <div className="mt-1 rounded-xl border border-[#e5e0d8] bg-white p-1 shadow-lg">
    {items.map((t) => (
      <div
        key={t}
        className={`flex items-center justify-between rounded-md px-2 py-1 text-[13px] font-semibold capitalize ${
          t === selected ? "bg-[#f3efea]" : ""
        }`}
      >
        {t}
        {t === selected && <Check className="h-3.5 w-3.5" />}
      </div>
    ))}
  </div>
);

export const SearchFormScreen = ({ mode = "name", query = "", type = "Any type", tkey = "Any key", fame = 80, lists, slim }) => {
  const c1 = fameColor(fame);
  const c2 = fameColor(Math.min(100, fame + 22));
  return (
    <div className="p-3">
      <div
        className="relative overflow-hidden rounded-[2rem] border border-[#e5e0d8] bg-[#fffdf9] p-5"
        style={{ boxShadow: `0 0 0 4px ${c1}12` }}
      >
        <h2 className="relative font-serif text-4xl font-bold">Find your tune</h2>
        <div className="relative mt-5">
          <div className="mb-2 flex gap-1.5">
            {[
              ["name", "By name"],
              ["notes", "By notes (ABC)"],
            ].map(([id, label]) => (
              <span
                key={id}
                className={`rounded-full px-3.5 py-1.5 text-xs font-extrabold ${
                  mode === id ? "bg-[#2d2824] text-white" : "border border-[#e5e0d8] bg-white text-[#6e655b]"
                }`}
              >
                {label}
              </span>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#a89e92]" />
            <div className="w-full truncate rounded-2xl border border-[#e5e0d8] bg-white py-4 pl-12 pr-4 text-lg font-semibold ring-2 ring-[#2d2824]/20">
              {query || <span className="font-normal text-[#a89e92]">Tune name or paste a track link…</span>}
            </div>
          </div>
          {!slim && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <Trigger label="Type of tune" value={type} />
              {lists?.types && <OpenList items={["Any type", ...lists.types]} selected={type} />}
            </div>
            <div>
              <Trigger label="Tonality / key" value={tkey} />
              {lists?.keys && <OpenList items={["Any key", ...lists.keys]} selected={tkey} />}
            </div>
          </div>
          )}
        </div>
        {!lists && !slim && (
          <>
            {!query && (
              <div className="relative mt-6 rounded-3xl border border-[#e5e0d8] bg-[#fbf9f4] p-5">
                <FameSlider value={fame} setValue={() => {}} />
              </div>
            )}
            <div
              className="relative mt-6 flex h-16 w-full items-center justify-center gap-3 rounded-full text-xl font-black text-white"
              style={{ background: `linear-gradient(120deg, ${c1}, ${c2})` }}
            >
              <Search className="h-7 w-7" strokeWidth={2.5} /> Search Tunes
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const ResultsScreen = ({ results = [], note, max = 2, linkInfo }) => (
  <div className="px-3 pb-3">
    {linkInfo?.recording && <AlbumNav linkInfo={linkInfo} onTrack={() => {}} />}
    <div className="mb-3 flex flex-wrap items-center gap-2 text-sm font-bold text-[#6e655b]">
      <span className="rounded-full bg-[#2d2824] px-3 py-1 text-white">
        {results.length} result{results.length === 1 ? "" : "s"}
      </span>
      {note && <span>{note}</span>}
    </div>
    <div className="grid grid-cols-1 gap-4">
      {results.slice(0, max).map((t, i) => (
        <TuneCard key={t.tune_id} tune={t} index={i} />
      ))}
    </div>
  </div>
);

const Stat = ({ v, k, red }) => (
  <div className="rounded-2xl border border-[#e5e0d8] bg-white p-3">
    <div className={`font-serif text-2xl font-bold ${red ? "text-[#e11d48]" : ""}`}>{v ?? "—"}</div>
    <div className="text-xs font-semibold text-[#6e655b]">{k}</div>
  </div>
);

// the tune's Info dialog, as in the Tune viewer
export const InfoScreen = ({ tune }) => (
  <div className="space-y-4 p-5">
    <div>
      <h3 className="font-serif text-3xl font-bold">{tune.name}</h3>
      <p className="text-sm capitalize text-[#6e655b]">
        {tune.type} · thesession #{tune.tune_id}
      </p>
    </div>
    {tune.aliases?.length > 0 && (
      <div>
        <p className="text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">Also known as</p>
        <p className="mt-1 text-sm">{tune.aliases.slice(0, 6).join(" · ")}</p>
      </div>
    )}
    <div>
      <p className="text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">Ranking</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Stat v={tune.fame_score} k="Fame score" red />
        <Stat v={tune.versions_count} k="Versions" />
        <Stat v={tune.recordings_count} k="Recordings" />
        <Stat v={tune.sets_count} k="In sets" />
        <Stat v={tune.tunebooks} k="Tunebooks" />
      </div>
      <div className="mt-2">
        <RankingVote tuneId={tune.tune_id} />
      </div>
    </div>
  </div>
);

export const AbcGuideScreen = () => (
  <div className="space-y-3 p-4">
    <h3 className="font-serif text-2xl font-bold">Reading &amp; writing ABC</h3>
    <p className="text-sm font-semibold text-[#6e655b]">
      ABC is plain text that sounds like music. Edit it in the tune viewer and the notation and the player follow
      instantly.
    </p>
    <AbcGuideBody />
  </div>
);

export const SOURCES = [
  ["Tidal", SiTidal, "#000000"],
  ["YouTube", SiYoutube, "#ff0000"],
  ["Spotify", SiSpotify, "#1db954"],
  ["Apple Music", SiApplemusic, "#fa2d48"],
  ["Deezer", FaDeezer, "#a238ff"],
  ["Bandcamp", SiBandcamp, "#1da0c3"],
];

export const SourcesScreen = () => (
  <div className="p-4">
    <p className="text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">Paste a track link from</p>
    <div className="mt-3 grid grid-cols-3 gap-2">
      {SOURCES.map(([n, Icon, c]) => (
        <div key={n} className="flex flex-col items-center gap-1.5 rounded-2xl border border-[#e5e0d8] bg-white p-3">
          <Icon className="h-7 w-7" style={{ color: c }} />
          <span className="text-xs font-extrabold">{n}</span>
        </div>
      ))}
    </div>
    <div className="mt-4 space-y-2">
      {[
        ["1", "Foinn.org reads the artist, the album and the track"],
        ["2", "finds that album among thesession.org recordings"],
        ["3", "and opens the tunes played on that very track"],
      ].map(([n, t]) => (
        <div key={n} className="flex items-center gap-2 rounded-2xl bg-white p-2.5 text-sm font-bold">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#8a2be2] text-xs text-white">{n}</span>
          {t}
        </div>
      ))}
    </div>
  </div>
);

// Tune viewer action row with the "found via" icon
export const TuneLinksScreen = ({ tune, source = "Tidal" }) => {
  const [, Icon, color] = SOURCES.find((s) => s[0] === source) || SOURCES[0];
  const btn = "flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-4 py-2 text-sm font-bold";
  return (
    <div className="p-4">
      <h3 className="font-serif text-3xl font-bold">{tune.name}</h3>
      <p className="text-sm capitalize text-[#6e655b]">{tune.type}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={btn}>
          <Info className="h-4 w-4" /> Info
        </span>
        <span className={btn}>
          <FileDown className="h-4 w-4" /> Export PDF
        </span>
        <span className={btn}>
          <ShieldQuestion className="h-4 w-4" /> Verify rarity
        </span>
        <span className={btn}>
          <Flag className="h-4 w-4" style={{ color: "#e11d48", fill: "#e11d48" }} /> To learn
        </span>
        <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#8a2be2] bg-white">
          <Icon className="h-4 w-4" style={{ color }} />
        </span>
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-2xl bg-[#2d2824] px-4 py-3 text-sm font-bold text-white">
        <Star className="h-4 w-4 text-[#c89bff]" /> {source} link copied — share the track you found it on
      </div>
      {tune.tune_id && (
        <div className="mt-4">
          <TuneCard tune={tune} index={0} />
        </div>
      )}
    </div>
  );
};
